import { defineStore } from "pinia";
import { ref, computed, Ref } from "vue";
import { ActivePin } from "./explorerTypes";
import { convertDates } from "chartjs-graph";
import dayjs from "dayjs";
import { useApiClient } from "@/api/client";
import ClickhouseDateTimeGranularity = App.Enums.ClickhouseDateTimeGranularity;
import { GetAnnotatedEventsParamsWithDate } from "@/api/resources/annotated-events";
import DocumentDO = App.Data.Explorer.DocumentDO;
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import { useDocumentHistory } from "@/modules/explorer/documentHistoryStore";
import { v4 as uuidv4 } from "uuid";
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import { categoryToFilterValue } from "@/Utils/FilterUtils";
import router from "@/router/router";
import DocumentViewTabs = App.Enums.DocumentViewTabs;

export const routeNameToViewTab: Record<string, DocumentViewTabs> = {
	"explorer.graphWrapper": "graph",
	"explorer.metricWrapper": "table",
	"explorer.singleMetricWrapper": "single-metric",
};

export const useExplorerStore = defineStore("explorerStore", () => {
	function createExplorerState() {
		// TODO: Type safety please?
		return {
			annotations: [],
			selectedFilter: null,
			activePins: [],
			openHierarchiesInExplorer: [],
			showMeasureDateTime: false,
			showDataFullScreen: false,
			pendingAlertAfterSave: false,
			lastUpdated: dayjs().toDate(),
			lastUpdatedInBackground: dayjs().toDate(),
			documentFetchedFromHistory: false,
			isDocumentLoaded: false,
			addDocumentToHistory: null,
			getPreviousDocument: null,
			getNextDocument: null,
			isShiftPressed: false,
			lastClickhouseQueryPerformance: {},
			lastClickhouseSql: "",
			lastClickhouseBaseDataSql: "",
			lastLegendDimensionValuesIds: [],
			isDataLoading: false,
			isDataLoadingInBackground: false,
			showDateComparisonBar: false,
			selectedComparisonYears: [],
			lastUsedMeasureDateTime: new Date(),
			areHotkeysEnabled: true,
			activeDataView: "graph" as App.Enums.DocumentViewTabs,
			lastOpenedSavedDocumentDocumentUuid: null,
			lastOpenedSavedDocument: null,
			dashboardRedirectUuid: null,
			dashboardCardDocumentToView: null as DocumentDO | null,
			highestMetricValue: 0,
			lowestMetricValue: 0,
			hiddenLabels: [],
			userAbilityForCurrentDocument: ["*"],
			isOpeningSavedDocument: false,
			showAiDrawer: false,
			redirectToAlertsAfterSave: false,
		};
	}

	const explorerState = ref(createExplorerState());

	const document = ref<DocumentDO>(null);
	const derivedDocument = ref<DocumentDO>(null);

	const isSavedDocument = computed(() => {
		return (
			explorerState.value.lastOpenedSavedDocumentDocumentUuid ===
			document.value.uuid
		);
	});

	const isOpenedButUnsaved = computed(() => {
		return (
			explorerState.value.lastOpenedSavedDocumentDocumentUuid &&
			!isSavedDocument.value
		);
	});

	function $reset() {
		explorerState.value = createExplorerState();
		document.value = null;
		derivedDocument.value = null;
	}

	const ACTIVE_DIMENSIONS = computed(() => {
		return document.value.active_filters.map(
			(activeFilter) => activeFilter.dimension.name_clean
		);
	});

	const ACTIVE_PINS = computed(() => {
		return explorerState.value.activePins.map(
			(activePin) => activePin.dimension.name_clean
		);
	});

	const hideLabel = (label: string) => {
		const hidden_labels = document.value?.hidden_labels;
		if (hidden_labels) {
			const index = hidden_labels.indexOf(label);
			if (index !== -1) {
				hidden_labels.splice(index, 1);
			}
		}
	};

	const toggleHiddenLabel = (label: string) => {
		if (!document.value) {
			// prevent error in dashboard
			return;
		}

		if (
			document.value.legend_dimension.granularity === "day" &&
			dayjs(label).isValid()
		) {
			label = dayjs(label.toLowerCase()).format("YYYY-MM-DD");
		}

		const { hidden_labels } = document.value;
		const index = hidden_labels.indexOf(label);

		if (index === -1) {
			hidden_labels.push(label);
		} else {
			hidden_labels.splice(index, 1);
		}

		// / ! \ - - - not the final solution - - - / ! \
		// todo create store document without refresh data method

		// update UUID's and add to history-chain
		document.value.parent_uuid = document.value.uuid;
		document.value.uuid = uuidv4();
		// add to history
		useDocumentHistory().addDocumentToHistory(document.value);

		// always reset this documentFetchedFromHistory again:
		explorerState.value.documentFetchedFromHistory = false;

		explorerState.value.lastUpdated = dayjs().toDate();
	};

	const fetchAnnotations = async () => {
		if (!derivedDocument.value.is_annotation_visibility_enabled) {
			explorerState.value.annotations = [];
			return;
		}

		const responses = await Promise.all(
			dateRangesForAnnotations.value.map((dateRange) =>
				useApiClient("explorer").getAnnotatedEvents(dateRange)
			)
		);

		const allEvents = responses.flatMap((response) =>
			response.isOk() ? response.value : []
		);

		const annotations = [
			...new Map(allEvents.map((event) => [event.id, event])).values(),
		];

		explorerState.value.annotations = annotations ?? [];
	};

	const FETCH_HOME_DOCUMENT = async (uuid: string | null) => {
		const response = await useApiClient("explorer").getEmptyDocument(uuid);
		if (response.isOk()) {
			const documentFromResponse = response.value;
			const oldDocument = document.value;

			// for hierarchy filters that were possibly open as Quick Filters
			if (oldDocument && Array.isArray(oldDocument.active_filters)) {
				const oldFilters = oldDocument.active_filters;

				// Ensure the documentFromResponse has active_filters
				if (Array.isArray(documentFromResponse.active_filters)) {
					documentFromResponse.active_filters =
						documentFromResponse.active_filters.map((newFilter) => {
							// Find matching filter from the old document by dimension.id
							const matchingOldFilter = oldFilters.find(
								(oldFilter) =>
									oldFilter?.dimension?.id === newFilter?.dimension?.id
							);

							// copy isQuickFilter state
							if (matchingOldFilter) {
								newFilter.isQuickFilter = matchingOldFilter.isQuickFilter;
								if (newFilter.isQuickFilter) {
									newFilter.isDefault = false;
								}
							}

							return newFilter;
						});
				}
			}

			const viewTab =
				routeNameToViewTab[String(router.currentRoute.value.name)];
			if (viewTab) {
				documentFromResponse.view_tab = viewTab;
			}

			document.value = convertDates(documentFromResponse);

			setTimeout(() => {
				explorerState.value.isDocumentLoaded = true;
			}, 10);

			// reset relation to a possible opened document
			explorerState.value.lastOpenedSavedDocumentDocumentUuid = null;
			explorerState.value.lastOpenedSavedDocument = null;
		}
	};

	const RESET_FILTERS = async () => {
		const response = await useApiClient("explorer").getDefaultFilters();
		if (response.isOk()) {
			document.value.active_filters = response.value;
		}
	};

	const APPLY_FILTER = (
		newFilter: App.Data.Explorer.Filter.ActiveFilterDTO,
		operation: string = "create"
	) => {
		const exists = document.value.active_filters.some(
			(activeFilter: App.Data.Explorer.Filter.ActiveFilterDTO) =>
				activeFilter.dimension.name_clean === newFilter.dimension.name_clean
		);

		// An "edit" with nothing to replace would silently drop the filter.
		if (operation === "create" || !exists) {
			document.value.active_filters.push(newFilter);
			return;
		}

		document.value.active_filters = document.value.active_filters.map(
			(activeFilter: App.Data.Explorer.Filter.ActiveFilterDTO) => {
				return activeFilter.dimension.name_clean ===
					newFilter.dimension.name_clean
					? newFilter
					: activeFilter;
			}
		);
	};

	const REMOVE_FILTER_BY_INDEX = (index: number) => {
		document.value.active_filters.splice(index, 1);
	};

	const TOGGLE_FILTER_INCLUSION = (index: number) => {
		document.value.active_filters[index].valuesAreIncluded =
			!document.value.active_filters[index].valuesAreIncluded;
	};

	const TOGGLE_FILTER_USAGE = (index: number) => {
		document.value.active_filters[index].isApplied =
			!document.value.active_filters[index].isApplied;
	};

	const TOGGLE_QUICK_FILTER = (index: number) => {
		document.value.active_filters[index].isQuickFilter =
			!document.value.active_filters[index].isQuickFilter;
	};

	const GET_ACTIVE_FILTER = (
		dimensionNameClean: string
	): App.Data.Explorer.Filter.ActiveFilterDTO => {
		return document.value.active_filters.find(
			(activeFilter) => activeFilter.dimension.name_clean === dimensionNameClean
		);
	};

	const GET_ACTIVE_FILTER_BY_GRANULARITY = (
		familyNameClean: string,
		granularity: ClickhouseDateTimeGranularity
	): App.Data.Explorer.Filter.ActiveFilterDTO => {
		return document.value.active_filters.find(
			(activeFilter) =>
				activeFilter.dimension.family_name_clean === familyNameClean &&
				activeFilter.dimension.granularity === granularity
		);
	};

	const ADD_PIN = (newPin: ActivePin, toggle: boolean = true) => {
		const index = explorerState.value.activePins.findIndex(
			(item) => item.dimension.name_clean === newPin.dimension.name_clean
		);

		if (index !== -1) {
			if (toggle) {
				explorerState.value.activePins.splice(index, 1);
			}
		} else {
			explorerState.value.activePins.push(newPin);
		}
	};

	const REMOVE_PIN = (pinToRemove: ActivePin) => {
		const index = explorerState.value.activePins.findIndex(
			(item) => item.dimension.name_clean === pinToRemove.dimension.name_clean
		);

		if (index !== -1) {
			explorerState.value.activePins.splice(index, 1);
		}
	};

	const GET_VISIBLE_PINS = () => {
		return explorerState.value.activePins.filter((activePin) =>
			ACTIVE_DIMENSIONS.value.includes(activePin.dimension.name_clean)
		);
	};
	const TOGGLE_QUICK_FILTER_MENU_FROM_PIN = async (pin: ActivePin) => {
		let filterIndex = document.value.active_filters.findIndex(
			(filter) => filter.dimension.name_clean === pin.dimension.name_clean
		);

		if (filterIndex === -1) {
			const dimension = usePropertyStore().dimensions.find(
				(dimension) => dimension.id === pin.dimension.id
			);
			if (!dimension) {
				console.error("Dimension not found for pin", pin.dimension.id);
				return;
			}

			// Nominal/bucketed quick filters start with an empty selection; the
			// menu loads its own options. Only hierarchy needs a seeded root node.
			let values = [];
			if (pin.dimension.type === "hierarchy") {
				const defaultHierarchyDefinition = dimension.hierarchy_definitions.find(
					(hd) => hd.is_default
				);
				if (!defaultHierarchyDefinition?.id) {
					console.error(
						"No default hierarchy definition found for dimension",
						dimension.id
					);
					return;
				}
				dimension.hierarchy_definition_id = defaultHierarchyDefinition.id;

				const fetchedValues = await useApiClient("explorer").getHierarchy(
					document.value,
					dimension.hierarchy_definition_id
				);
				if (!fetchedValues.isOk()) {
					console.error("Error getting hierarchy values");
					return;
				}
				const nodes = fetchedValues.value as unknown as QuickFilterNodeDTO[];
				if (nodes.length === 0) {
					console.error("No first node found in hierarchy values");
					return;
				}
				values = [categoryToFilterValue(nodes[0])];
			} else if (pin.dimension.type === "bucketed") {
				const defaultBucket = dimension.dimension_buckets?.find(
					(bucket) => bucket.is_default
				);
				if (!defaultBucket?.id) {
					console.error("No default bucket found for dimension", dimension.id);
					return;
				}
				dimension.dimension_bucket_id = defaultBucket.id;
			}

			const newFilter: App.Data.Explorer.Filter.ActiveFilterDTO = {
				dimension: dimension,
				values: values,
				isApplied: true,
				valuesAreIncluded: pin.valuesAreIncluded,
				isQuickFilter: undefined,
				isDefault: false,
				sortBy: null,
				sortDirection: null,
			};

			APPLY_FILTER(newFilter, "create");
			filterIndex = document.value.active_filters.findIndex(
				(filter) =>
					filter.dimension.name_clean === newFilter.dimension.name_clean
			);
		}
		TOGGLE_QUICK_FILTER(filterIndex);
	};

	const verticalSupportsBudget = computed(() =>
		[
			"revenuesdrivein",
			"revenuesdriveout",
			"advertising_revenue_distr",
		].includes(document.value.vertical.name_clean)
	);

	const dateRangesForAnnotations = computed(() => {
		const ranges: GetAnnotatedEventsParamsWithDate[] = [
			{
				from: dayjs(document.value.period_selection.from_parsed).toDate(),
				to: dayjs(document.value.period_selection.to_parsed).toDate(),
				applies_to_subject_date: true,
			},
		];

		if (document.value.horizontal_dimension?.family_name_clean === "buildup") {
			ranges.push({
				from: dayjs(document.value.period_selection.from_parsed)
					.subtract(document.value.period_build_up_window, "day")
					.toDate(),
				to: dayjs(document.value.period_selection.to_parsed).toDate(),
				applies_to_measure_date: true,
			});
		}

		return ranges;
	});

	const updateLoadingState = (
		value: boolean,
		inBackground: boolean,
		isLoading: Ref<boolean>
	) => {
		explorerState.value.isDataLoading = value;
		if (inBackground) {
			if (!value) {
				setTimeout(() => {
					explorerState.value.isDataLoadingInBackground = value;
				}, 500);
			} else {
				explorerState.value.isDataLoadingInBackground = value;
			}
		} else {
			isLoading.value = value;
		}
	};

	return {
		dateRangesForAnnotations,
		explorerState,
		document,
		derivedDocument,
		ACTIVE_DIMENSIONS,
		$reset,
		ACTIVE_PINS,
		FETCH_HOME_DOCUMENT,
		RESET_FILTERS,
		APPLY_FILTER,
		REMOVE_FILTER_BY_INDEX,
		TOGGLE_FILTER_INCLUSION,
		TOGGLE_FILTER_USAGE,
		GET_ACTIVE_FILTER,
		GET_ACTIVE_FILTER_BY_GRANULARITY,
		ADD_PIN,
		REMOVE_PIN,
		GET_VISIBLE_PINS,
		TOGGLE_QUICK_FILTER_MENU_FROM_PIN,
		TOGGLE_QUICK_FILTER,
		toggleHiddenLabel,
		hideLabel,
		isSavedDocument,
		isOpenedButUnsaved,
		verticalSupportsBudget,
		fetchAnnotations,
		updateLoadingState,
	};
});
